import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Eye, Loader2, Save, Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { friendlyError } from "@/lib/friendlyError";
import {
  SECTION_DEF_BY_KEY,
  WELCOME_PACKET_STATUS_LABEL,
  WelcomePacketSection,
  WelcomePacketStatus,
  emptyPacketSections,
  loadPacketRow,
  savePacketRow,
  setPacketStatus,
} from "@/lib/welcomePacket";
import { WelcomePacketPreviewDialog } from "./WelcomePacketPreviewDialog";

const CLASS_SCOPE = "__class__";

interface Props {
  classSlug: string;
  className: string;
  /** Session labels from the class schedule, used for session overrides. */
  sessionLabels: string[];
  /** Lets the parent dialog know the packet has unsaved edits. */
  onDirtyChange?: (dirty: boolean) => void;
  /**
   * Hands the parent a save function so the dialog's main Save also persists
   * the packet. Called with `null` on unmount.
   */
  registerSave?: (fn: (() => Promise<void>) | null) => void;
}

/**
 * Welcome Packet tab. Content is stored per class (and optionally per session)
 * in `class_welcome_packets` and reused by every email and admin surface.
 */
export function WelcomePacketTab({
  classSlug,
  className,
  sessionLabels,
  onDirtyChange,
  registerSave,
}: Props) {
  const [scope, setScope] = useState<string>(CLASS_SCOPE);
  const [sections, setSections] = useState<WelcomePacketSection[]>(emptyPacketSections());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [status, setStatus] = useState<WelcomePacketStatus>("draft");
  const [statusMeta, setStatusMeta] = useState<{ at: string | null; by: string | null }>({
    at: null,
    by: null,
  });
  const [statusSaving, setStatusSaving] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [testing, setTesting] = useState(false);

  const sessionLabel = scope === CLASS_SCOPE ? null : scope;

  // Latest values, so the save function handed to the parent is never stale.
  const latest = useRef({ classSlug, sessionLabel, sections, dirty });
  latest.current = { classSlug, sessionLabel, sections, dirty };

  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  useEffect(() => {
    if (!registerSave) return;
    registerSave(async () => {
      const cur = latest.current;
      // Always write — a stale `dirty` flag must never swallow real edits.
      await savePacketRow(cur.classSlug, cur.sessionLabel, cur.sections);
      setDirty(false);
    });
    return () => registerSave(null);
  }, [registerSave]);


  const load = useCallback(async () => {
    setLoading(true);
    try {
      const row = await loadPacketRow(classSlug, sessionLabel);
      setSections(row.sections);
      setStatus(row.status);
      setStatusMeta({ at: row.statusChangedAt, by: row.statusChangedByEmail });
      setDirty(false);
    } catch (e) {
      friendlyError({
        what: "I couldn't load the welcome packet.",
        nextStep: "Refresh the class and try again.",
        technical: e,
      });
    } finally {
      setLoading(false);
    }
  }, [classSlug, sessionLabel]);

  useEffect(() => {
    void load();
  }, [load]);

  const update = (key: string, patch: Partial<WelcomePacketSection>) => {
    setSections((prev) => prev.map((s) => (s.key === key ? { ...s, ...patch } : s)));
    setDirty(true);
  };

  const enabledCount = useMemo(
    () =>
      sections.filter(
        (s) => s.enabled && ((s.content ?? "").trim() || (s.linkUrl ?? "").trim()),
      ).length,
    [sections],
  );

  const changeStatus = async (next: WelcomePacketStatus) => {
    setStatusSaving(true);
    try {
      if (dirty) await savePacketRow(classSlug, sessionLabel, sections);
      await setPacketStatus(classSlug, sessionLabel, next);
      setStatus(next);
      setStatusMeta({ at: new Date().toISOString(), by: null });
      setDirty(false);
      toast.success(
        next === "ready"
          ? "Packet marked Ready — it will send with new registrations"
          : next === "paused"
            ? "Packet paused — families will not receive it"
            : "Packet moved back to Draft",
      );
      void load();
    } catch (e) {
      friendlyError({ what: "I couldn't change the packet status.", technical: e });
    } finally {
      setStatusSaving(false);
    }
  };

  const sendTest = async () => {
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(testEmail.trim())) {
      toast.error("Enter a valid administrator email address");
      return;
    }
    setTesting(true);
    try {
      if (dirty) await savePacketRow(classSlug, sessionLabel, sections);
      const { data, error } = await supabase.functions.invoke("welcome-packet-admin", {
        body: {
          action: "test",
          classId: classSlug,
          className,
          sessionLabel,
          testEmail: testEmail.trim(),
        },
      });
      if (error) throw error;
      if ((data as any)?.error) throw new Error(String((data as any).error));
      setDirty(false);
      toast.success(`Test queued for ${testEmail.trim()}`, {
        description:
          "Accepted into the send queue — only this address is targeted. Check Settings → Communications to confirm it was actually sent.",
      });
    } catch (e) {
      friendlyError({
        what: "I couldn't queue the welcome packet test.",
        changed: "No test email was queued.",
        technical: e,
      });
    } finally {
      setTesting(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await savePacketRow(classSlug, sessionLabel, sections);
      setDirty(false);
      toast.success(
        sessionLabel ? `Saved session overrides for ${sessionLabel}` : "Welcome packet saved",
      );
    } catch (e) {
      friendlyError({ what: "I couldn't save the welcome packet.", technical: e });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-base font-semibold">Welcome Packet</h3>
        <p className="text-sm text-muted-foreground">
          Onboarding details families receive after registering. Only sections that are turned on
          and have content appear in the email — blank sections are hidden.
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1.5">
          <Label className="text-xs">Editing</Label>
          <Select value={scope} onValueChange={setScope}>
            <SelectTrigger className="w-[280px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={CLASS_SCOPE}>Class-level packet (all sessions)</SelectItem>
              {sessionLabels.map((label) => (
                <SelectItem key={label} value={label}>
                  Session override — {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button type="button" variant="outline" onClick={() => setPreviewOpen(true)}>
          <Eye className="h-4 w-4 mr-1.5" /> Preview parent email
        </Button>
        <Button type="button" onClick={save} disabled={saving || loading}>
          {saving ? (
            <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
          ) : (
            <Save className="h-4 w-4 mr-1.5" />
          )}
          Save packet
        </Button>
      </div>

      {sessionLabel ? (
        <Alert>
          <AlertDescription>
            Session overrides win over the class-level packet. Leave a field blank here to keep the
            class-level wording for <strong>{sessionLabel}</strong>.
          </AlertDescription>
        </Alert>
      ) : null}

      <div className="rounded-lg border p-4 space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <p className="text-sm font-medium flex items-center gap-2">
              Packet status
              <Badge variant={status === "ready" ? "default" : "secondary"}>
                {status === "ready" ? "Ready" : status === "paused" ? "Paused" : "Draft"}
              </Badge>
            </p>
            <p className="text-xs text-muted-foreground">
              {WELCOME_PACKET_STATUS_LABEL[status]}
              {statusMeta.at
                ? ` · changed ${new Date(statusMeta.at).toLocaleString()}${statusMeta.by ? ` by ${statusMeta.by}` : ""}`
                : ""}
            </p>
          </div>
          <Select
            value={status}
            onValueChange={(v) => changeStatus(v as WelcomePacketStatus)}
            disabled={statusSaving}
          >
            <SelectTrigger className="w-[200px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="ready">Ready</SelectItem>
              <SelectItem value="paused">Paused</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <p className="text-xs text-muted-foreground">
          Families only receive this packet when it is marked <strong>Ready</strong>. Publishing the
          class does not change this. While it is Draft or Paused, registrations still get the
          standard confirmation email and the skipped packet is recorded in Settings →
          Communications.
        </p>
        <div className="flex flex-wrap items-end gap-2">
          <div className="space-y-1.5">
            <Label className="text-xs">Send a test to an admin address</Label>
            <Input
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder="you@example.com"
              className="w-[260px]"
            />
          </div>
          <Button type="button" variant="outline" onClick={sendTest} disabled={testing}>
            {testing ? (
              <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
            ) : (
              <Send className="h-4 w-4 mr-1.5" />
            )}
            Send test
          </Button>
        </div>
      </div>

      <p className="text-xs text-muted-foreground">
        {enabledCount} section{enabledCount === 1 ? "" : "s"} will appear in the email.
      </p>

      {loading ? (
        <div className="py-10 flex items-center gap-2 text-muted-foreground text-sm">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : (
        <div className="space-y-3">
          {sections.map((s) => {
            const def = SECTION_DEF_BY_KEY[s.key];
            return (
              <div key={s.key} className="rounded-lg border p-4 space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium">{def?.label ?? s.heading}</p>
                    <p className="text-xs text-muted-foreground">
                      {s.enabled ? "Shown in packet" : "Hidden from packet"}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label htmlFor={`show-${s.key}`} className="text-xs">
                      Show in packet
                    </Label>
                    <Switch
                      id={`show-${s.key}`}
                      checked={s.enabled}
                      onCheckedChange={(v) => update(s.key, { enabled: v })}
                    />
                  </div>
                </div>

                {s.enabled ? (
                  <div className="space-y-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs">Heading</Label>
                      <Input
                        value={s.heading}
                        onChange={(e) => update(s.key, { heading: e.target.value })}
                        placeholder={def?.defaultHeading}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Content</Label>
                      <Textarea
                        rows={3}
                        value={s.content}
                        onChange={(e) => update(s.key, { content: e.target.value })}
                        placeholder={def?.placeholder}
                      />
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="space-y-1.5">
                        <Label className="text-xs">Attachment or link (optional)</Label>
                        <Input
                          value={s.linkUrl ?? ""}
                          onChange={(e) => update(s.key, { linkUrl: e.target.value })}
                          placeholder="https://…"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs">Link label (optional)</Label>
                        <Input
                          value={s.linkLabel ?? ""}
                          onChange={(e) => update(s.key, { linkLabel: e.target.value })}
                          placeholder="Download the supply list"
                        />
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}

      <WelcomePacketPreviewDialog
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        classSlug={classSlug}
        className={className}
        sessionLabel={sessionLabel}
      />
    </div>
  );
}

export default WelcomePacketTab;
